<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import type { VueInspectorClient } from "vite-plugin-vue-inspector";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import { backendOld } from "~/api";
import AdminDropdownBackendSummary from "~/components/AdminDropdownBackendSummary.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import { copyAndNotify } from "~/components/notifications";
import { ORDERED_SUBSCRIPTIONS } from "~/constants/billing";
import { ORDERED_ROLES } from "~/constants/user";
import { CommandId, DropdownMenuItemKind, Placement, Subscription, TutorialName } from "~/shared/enums";
import { useDataStore, useEnvironmentStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { randomSample } from "~/utils/common";

const router = useRouter();
const dataStore = useDataStore();
const environmentStore = useEnvironmentStore();
const pageStore = usePageStore();
const userStore = useUserStore();
const tenantStore = useTenantStore();

const transactionBarrage = async () => {
  const task = randomSample(dataStore.getAllLoadedTasksForUiOnly())[0];
  await actions.visualization.navigateToTask(task.duid);
  const tagProperty = dataStore.defaultTagsProperty;
  const tags = dataStore.getOptionList(tagProperty);
  tags.forEach((tag, i) => {
    // eslint-disable-next-line no-restricted-syntax
    setTimeout(async () => {
      const result = await dataStore.addOptions(tagProperty, [task.duid], [tag.duid], { awaitBackend: true });
      // eslint-disable-next-line no-console
      console.log(new Date().toISOString(), tag.title, result);
    }, i * 50);
  });
};

const makeTeammateLoginToken = async () => {
  const loginToken = (await backendOld.auth.makeTeammateLoginTokenAdmin()).data;
  copyAndNotify("Login token", loginToken);
};

const completeAllTutorials = () => {
  userStore.updateTutorialStatuses(
    Object.values(TutorialName)
      .filter((e): e is number => typeof e === "number")
      .map((e) => ({ name: e, status: 1000 }))
  );
};

const resetAllTutorials = () => {
  userStore.updateTutorialStatuses(
    Object.values(TutorialName)
      .filter((e): e is number => typeof e === "number")
      .map((e) => ({ name: e, status: 1 })),
    true
  );
};

const toggleDebugInfoShown = () => {
  pageStore.setShowTiming(!pageStore.showDebugInfo);
};

const toggleTouchCapabilities = () => {
  pageStore.setIsTouch(!pageStore.hasTouch);
};

const testSentryFrontend = () => {
  // TODO figure out how to get this past the dedupe filter
  Sentry.captureMessage("Intentional Sentry message");
};

const testSentryBackend = () => {
  backendOld.createTestError();
};

const sendConsoleToSentry = () => {
  Sentry.captureMessage(JSON.stringify(pageStore.consoleData));
};

const previewTitles = ref<(string | null)[]>([null, null, null]);
const updatePreviews = async () => {
  previewTitles.value = (await backendOld.auth.getPreviewTitlesDart()).data.titles;
};
updatePreviews();

const backendPort = __BACKEND_PORT__;
const frontendPort = __FRONTEND_PORT__;

const adminNavigation = computed(() =>
  [
    {
      title: "Local",
      base: `http://localhost:${frontendPort}`,
      disabled: environmentStore.isLocalStandard || environmentStore.isLocalNetwork,
      disabledReason: "Current environment",
    },
    {
      title: "Local build",
      base: `http://localhost:${backendPort}`,
      disabled: environmentStore.isLocalBuild,
      disabledReason: "Current environment",
    },
    {
      title: "Proxy",
      base: "https://dart.ngrok.app",
      disabled: environmentStore.isProxy,
      disabledReason: "Current environment",
    },
    {
      title: `Preview 1${previewTitles.value[0] ? ` - ${previewTitles.value[0]}` : ""}`,
      base: "https://preview-1.dartai.com",
      disabled: environmentStore.isPreview1 || previewTitles.value[0] === null,
      disabledReason: environmentStore.isPreview1 ? "Current environment" : "Not in use",
    },
    {
      title: `Preview 2${previewTitles.value[1] ? ` - ${previewTitles.value[1]}` : ""}`,
      base: "https://preview-2.dartai.com",
      disabled: environmentStore.isPreview2 || previewTitles.value[1] === null,
      disabledReason: environmentStore.isPreview2 ? "Current environment" : "Not in use",
    },
    {
      title: `Preview 3${previewTitles.value[2] ? ` - ${previewTitles.value[2]}` : ""}`,
      base: "https://preview-3.dartai.com",
      disabled: environmentStore.isPreview3 || previewTitles.value[2] === null,
      disabledReason: environmentStore.isPreview3 ? "Current environment" : "Not in use",
    },
    {
      title: "Staging",
      base: "https://stag.dartai.com",
      disabled: environmentStore.isStag,
      disabledReason: "Current environment",
    },
    {
      title: "Production",
      base: "https://app.dartai.com",
      disabled: environmentStore.isProd,
      disabledReason: "Current environment",
    },
  ].map((e) => ({
    ...e,
    navigate: {
      to: e.base + router.currentRoute.value.fullPath,
      newTab: !pageStore.isNativeApp,
    },
    kind: DropdownMenuItemKind.EXTERNAL_LINK,
  }))
);

const toggleMobileItem = computed(() => {
  let [curr, alt] = ["desktop", "mobile"];
  if (pageStore.isMobile) {
    [curr, alt] = [alt, curr];
  }
  return {
    title: `Switch from ${curr} to ${alt}`,
    kind: DropdownMenuItemKind.BUTTON,
    onClick: () => {
      if (pageStore.isDesktop) {
        pageStore.isIos = pageStore.isMac;
        pageStore.isMac = false;
        pageStore.isAndroid = pageStore.isWindows;
        pageStore.isWindows = false;
        pageStore.isLinux = false;
        pageStore.isDesktop = false;
        pageStore.isMobileApp = pageStore.isDesktopApp;
        pageStore.isDesktopApp = false;
        pageStore.isMobile = true;
        pageStore.isNativeApp = pageStore.isMobileApp;
      } else {
        pageStore.isMac = pageStore.isIos;
        pageStore.isWindows = pageStore.isAndroid;
        pageStore.isLinux = false;
        pageStore.isDesktop = true;
        pageStore.isDesktopApp = pageStore.isMobileApp;
        pageStore.isIos = false;
        pageStore.isAndroid = false;
        pageStore.isMobile = false;
        pageStore.isMobileApp = false;
        pageStore.isNativeApp = pageStore.isDesktopApp;
      }
    },
  };
});
const toggleAppItem = computed(() => {
  let [curr, alt] = ["browser", "app"];
  if (pageStore.isNativeApp) {
    [curr, alt] = [alt, curr];
  }
  return {
    title: `Switch from ${curr} to ${alt}`,
    kind: DropdownMenuItemKind.BUTTON,
    onClick: () => {
      if (pageStore.isNativeApp) {
        pageStore.isDesktopApp = false;
        pageStore.isMobileApp = false;
        pageStore.isNativeApp = false;
      } else {
        if (pageStore.isDesktop) {
          pageStore.isDesktopApp = true;
          pageStore.isMobileApp = false;
        } else {
          pageStore.isDesktopApp = false;
          pageStore.isMobileApp = true;
        }
        pageStore.isNativeApp = true;
      }
    },
  };
});
const toggleOsItems = computed(() => {
  if (pageStore.isMobile) {
    const curr = pageStore.isIos ? "iOS" : "Android";
    return ["iOS", "Android"]
      .filter((e) => e !== curr)
      .map((alt) => ({
        title: `Switch from ${curr} to ${alt}`,
        kind: DropdownMenuItemKind.BUTTON,
        onClick: () => {
          pageStore.isMac = false;
          pageStore.isWindows = false;
          pageStore.isLinux = false;
          pageStore.isIos = alt === "iOS";
          pageStore.isAndroid = alt === "Android";
        },
      }));
  }

  const curr = pageStore.isMac ? "MacOS" : pageStore.isWindows ? "Windows" : "Linux";
  return ["MacOS", "Windows", "Linux"]
    .filter((e) => e !== curr)
    .map((alt) => ({
      title: `Switch from ${curr} to ${alt}`,
      kind: DropdownMenuItemKind.BUTTON,
      onClick: () => {
        pageStore.isMac = alt === "MacOS";
        pageStore.isWindows = alt === "Windows";
        pageStore.isLinux = alt === "Linux";
        pageStore.isIos = false;
        pageStore.isAndroid = false;
      },
    }));
});

const dropdownSections = computed(() => [
  {
    title: "Links",
    items: [
      {
        title: "Superadmin links",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Superadmin links",
          sections: [
            {
              title: "Superadmin links",
              items: [
                {
                  title: "Database admin",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  hidden: !userStore.isAdmin,
                  navigate: {
                    to: "/api/admin/",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Cluster (Argo)",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  hidden: !userStore.isAdmin,
                  disabled: !(environmentStore.isProd || environmentStore.isStag),
                  navigate: {
                    to: `https://argocd.${environmentStore.isStag ? "stag." : ""}dartai.com`,
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Monitoring (Grafana)",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  hidden: !userStore.isAdmin,
                  disabled: !(environmentStore.isProd || environmentStore.isStag),
                  navigate: {
                    to: `https://monitoring.${environmentStore.isStag ? "stag." : ""}dartai.com`,
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Celery (Flower)",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  hidden: !userStore.isAdmin,
                  disabled: !(environmentStore.isProd || environmentStore.isStag),
                  navigate: {
                    to: `https://flower.${environmentStore.isStag ? "stag." : ""}dartai.com`,
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Make teammate login token",
                  kind: DropdownMenuItemKind.BUTTON,
                  hidden: !userStore.isAdmin,
                  onClick: makeTeammateLoginToken,
                },
                {
                  title: "Superuser dashboard",
                  // TODO open in new tab
                  kind: DropdownMenuItemKind.INTERNAL_LINK,
                  hidden: !userStore.isAdmin,
                  navigate: { to: { name: "superuser_dashboard" } },
                },
              ],
            },
          ],
        },
      },
      {
        title: "Admin links",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Admin links",
          sections: [
            {
              title: "Admin links",
              items: [
                {
                  title: "Internal API docs",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: "/api/v0/docs/",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Internal API schema",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: "/api/v0/schema/",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Storybook",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: !environmentStore.isLocal ? "/static/dist/storybook/index.html" : "http://localhost:6006/",
                    newTab: !pageStore.isMobileApp,
                  },
                  disabled: environmentStore.isProd,
                },
                {
                  title: "Brand guidelines",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: "https://dartai.com/brand",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Internal status page",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: "https://dart-internal.betteruptime.com",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Behavior exceptions",
                  kind: DropdownMenuItemKind.EXTERNAL_LINK,
                  navigate: {
                    to: "https://github.com/its-dart/dart/blob/main/docs/Behavior%20exceptions.md",
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Reset password",
                  kind: DropdownMenuItemKind.INTERNAL_LINK,
                  navigate: {
                    to: { name: "reset_password" },
                    newTab: !pageStore.isMobileApp,
                  },
                },
                {
                  title: "Console",
                  kind: DropdownMenuItemKind.INTERNAL_LINK,
                  navigate: { to: { name: "console" } },
                },
                {
                  title: "Latency report",
                  kind: DropdownMenuItemKind.INTERNAL_LINK,
                  navigate: { to: { name: "latency" } },
                },
              ],
            },
          ],
        },
      },
      {
        title: "Other environments",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Other environments",
          sections: [
            {
              title: "Other environments",
              items: adminNavigation.value,
            },
          ],
        },
      },
    ],
  },
  {
    title: "Configuration",
    items: [
      {
        title: "Open inspector",
        kind: DropdownMenuItemKind.BUTTON,
        hidden: !environmentStore.isLocalStandard,
        commandId: CommandId.OPEN_VUE_INSPECTOR,
        onClick: () => {
          const inspector = (window as unknown as { __VUE_INSPECTOR__: VueInspectorClient | undefined })
            .__VUE_INSPECTOR__;
          inspector?.enable();
        },
      },
      {
        title: "Debug",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Debug",
          sections: [
            {
              title: "Debug",
              items: [
                {
                  title: "Role override",
                  kind: DropdownMenuItemKind.COMPONENT,
                  component: GenericSubdropdown,
                  componentArgs: {
                    title: "Role override",
                    sections: [
                      {
                        title: "Role override",
                        items: [
                          ...ORDERED_ROLES.map((role) => ({
                            title: role,
                            disabled: userStore.roleNorm === role,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => userStore.setSessionRoleOverride(role),
                          })),
                          {
                            title: "Clear",
                            disabled: !userStore.sessionRoleOverride,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => userStore.setSessionRoleOverride(null),
                          },
                        ],
                      },
                    ],
                  },
                },
                {
                  title: "Subscription override",
                  kind: DropdownMenuItemKind.COMPONENT,
                  component: GenericSubdropdown,
                  componentArgs: {
                    title: "Subscription override",
                    sections: [
                      {
                        title: "Subscription override",
                        items: [
                          ...ORDERED_SUBSCRIPTIONS.filter((sub) => sub !== Subscription.APP_SUMO).map((sub) => ({
                            title: sub,
                            disabled: tenantStore.subscriptionNorm === sub,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => {
                              tenantStore.setSessionSubscriptionOverride(sub);
                            },
                          })),
                          {
                            title: "Clear",
                            disabled: !tenantStore.sessionSubscriptionOverride,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => {
                              tenantStore.setSessionSubscriptionOverride(null);
                            },
                          },
                        ],
                      },
                    ],
                  },
                },
                {
                  title: "AppSumo tier override",
                  kind: DropdownMenuItemKind.COMPONENT,
                  component: GenericSubdropdown,
                  componentArgs: {
                    title: "AppSumo tier override",
                    sections: [
                      {
                        title: "AppSumo tier override",
                        items: [
                          ...[1, 2, 3, 4, 5].map((tier) => ({
                            title: `Tier ${tier}`,
                            disabled: tenantStore.appSumoTierNorm === tier,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => {
                              tenantStore.setSessionAppSumoTierOverride(tier);
                              if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
                                tenantStore.setSessionSubscriptionOverride(Subscription.APP_SUMO);
                              }
                            },
                          })),
                          {
                            title: "Clear",
                            disabled: !tenantStore.sessionAppSumoTierOverride,
                            kind: DropdownMenuItemKind.BUTTON,
                            onClick: () => {
                              tenantStore.setSessionAppSumoTierOverride(null);
                              if (tenantStore.sessionSubscriptionOverride === Subscription.APP_SUMO) {
                                tenantStore.setSessionSubscriptionOverride(null);
                              }
                            },
                          },
                        ],
                      },
                    ],
                  },
                },
                {
                  title: `${pageStore.showDebugInfo ? "Hide" : "Show"} debug info`,
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: toggleDebugInfoShown,
                },
                {
                  title: "Clear all local data",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: () => actions.app.clearAllLocalData(),
                },
                {
                  title: `${pageStore.isOnline ? "Disable" : "Enable"} internet connection`,
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: () => {
                    pageStore.isOnline = !pageStore.isOnline;
                  },
                },
                {
                  title: `${pageStore.networkDelay ? "Disable" : "Enable"} network delay`,
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: () => {
                    pageStore.setNetworkDelay(!pageStore.networkDelay);
                  },
                },
                {
                  title: "Transaction barrage",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: transactionBarrage,
                },
              ],
            },
          ],
        },
      },
      {
        title: "Appearance",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Appearance",
          sections: [
            {
              title: "Appearance",
              items: [
                {
                  title: "Hide admin",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: () => {
                    pageStore.adminHidden = true;
                  },
                },
                {
                  title: "Complete all tutorials",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: completeAllTutorials,
                },
                {
                  title: "Reset all tutorials",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: resetAllTutorials,
                },
              ],
            },
          ],
        },
      },
      {
        title: "Platform",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Platform",
          widthPixels: 280,
          sections: [
            {
              title: "Platform",
              items: [
                toggleMobileItem.value,
                toggleAppItem.value,
                ...toggleOsItems.value,
                {
                  title: `Turn touch ${pageStore.hasTouch ? "off" : "on"}`,
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: toggleTouchCapabilities,
                },
              ],
            },
          ],
        },
      },
    ],
  },
  {
    title: "Third party tools",
    items: [
      {
        title: "Sentry",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Sentry",
          sections: [
            {
              title: "Sentry",
              items: [
                {
                  title: "Test Sentry frontend",
                  kind: DropdownMenuItemKind.BUTTON,
                  disabled: !pageStore.runTrackingTools,
                  onClick: testSentryFrontend,
                },
                {
                  title: "Test Sentry backend",
                  kind: DropdownMenuItemKind.BUTTON,
                  disabled: !pageStore.runTrackingTools,
                  onClick: testSentryBackend,
                },
                {
                  title: "Send console to Sentry",
                  kind: DropdownMenuItemKind.BUTTON,
                  disabled: !pageStore.runTrackingTools,
                  onClick: sendConsoleToSentry,
                },
              ],
            },
          ],
        },
      },
      {
        title: "Stripe",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Stripe",
          sections: [
            {
              title: "Stripe",
              items: [
                {
                  title: "Reset Stripe account",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: backendOld.billing.resetAccount,
                },
                {
                  title: "Delete Stripe subscriptions",
                  kind: DropdownMenuItemKind.BUTTON,
                  onClick: backendOld.billing.deleteSubscriptions,
                },
              ],
            },
          ],
        },
      },
    ],
  },
  {
    title: "Deployment info",
    items: [
      {
        title: "Deployment info",
        kind: DropdownMenuItemKind.COMPONENT,
        component: GenericSubdropdown,
        componentArgs: {
          title: "Deployment info",
          sections: [
            {
              title: "Deployment info",
              items: [
                {
                  title: "Deployment info",
                  kind: DropdownMenuItemKind.COMPONENT,
                  component: AdminDropdownBackendSummary,
                  noFocus: true,
                },
              ],
            },
          ],
        },
      },
    ],
  },
]);
</script>

<template>
  <DropdownMenu
    :sections="dropdownSections"
    :placement="Placement.RIGHT_BOTTOM"
    :distance="0"
    :on-after-open="updatePreviews">
    <slot />
  </DropdownMenu>
</template>
